
import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import {
  Map as MapIcon,
  Truck,
  Search,
  Layers,
  CheckCircle,
  Send,
  Clock,
  Trash2,
  Car,
  Route,
  Timer,
  Gauge,
  ArrowLeft,
  ChevronRight,
  ChevronLeft,
  Calendar,
  AlertCircle,
  StopCircle,
  Maximize,
} from "lucide-react";

import "../Style/TripsDashboard.css";
const CARTO_API_KEY = "cb1_2bxr_1_d729197da25d2aa64c8568bf";
const reverseGeocode = async (lat, lng) => {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`,
      {
        headers: {
          Accept: "application/json",
          "User-Agent": "TripDashboard/1.0",
        },
      },
    );

    const data = await res.json();
    return data.display_name || "Address not available";
  } catch (error) {
    console.error("Reverse geocode error:", error);
    return "Address not available";
  }
};

const useLeaflet = () => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (window.L && typeof window.L.map === "function") {
      setLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    script.async = true;

    const onLoad = () => {
      let attempts = 0;
      const interval = setInterval(() => {
        attempts++;
        if (window.L && typeof window.L.map === "function") {
          setLoaded(true);
          clearInterval(interval);
        }
        if (attempts > 50) clearInterval(interval);
      }, 50);
    };

    script.onload = onLoad;
    document.body.appendChild(script);

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
    document.head.appendChild(link);
  }, []);

  return loaded;
};

const formatDuration = (minutes) => {
  if (minutes === undefined || minutes === null) return "N/A";
  const totalMinutes = Math.round(minutes);
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${hours}h ${String(mins).padStart(2, "0")}m`;
};

const calculateDuration = (start, end) => {
  if (!start || !end) return "N/A";
  const startDate = new Date(start);
  const endDate = new Date(end);
  const diff = endDate - startDate;
  if (isNaN(diff)) return "N/A";
  const totalMinutes = Math.round(diff / (1000 * 60));
  return formatDuration(totalMinutes);
};

const getStatusIcon = (label) => {
  const icons = {
    All: Layers,
    Completed: CheckCircle,
    "In progress": Send,
    Expired: Clock,
    Deleted: Trash2,
  };
  return icons[label] || Layers;
};

const TripMap = ({ pathCoordinates, selectedTrip }) => {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const polylineRef = useRef(null);
  const markersRef = useRef([]);
  const isLeafletLoaded = useLeaflet();
  const [playIndex, setPlayIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const playbackMarkerRef = useRef(null);
  const [speed, setSpeed] = useState(1);

  const animateMarker = (start, end, duration) => {
    let startTime = null;

    const animate = (time) => {
      if (!startTime) startTime = time;
      const progress = Math.min((time - startTime) / duration, 1);
      const ease = (t) => t * t * (3 - 2 * t);
      const eased = ease(progress);
      const lat = start[0] + (end[0] - start[0]) * eased;
      const lng = start[1] + (end[1] - start[1]) * eased;
      const newPos = [lat, lng];

      playbackMarkerRef.current.setLatLng(newPos);
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  };

  useEffect(() => {
    if (
      isLeafletLoaded &&
      mapContainerRef.current &&
      !mapInstanceRef.current &&
      window.L
    ) {
      const L = window.L;

      try {
        mapInstanceRef.current = L.map(mapContainerRef.current).setView(
          [18.5204, 73.8567],
          13,
        );

        L.tileLayer(
          `https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png?key=${CARTO_API_KEY}`,
          {
            attribution:
              '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, &copy; <a href="https://carto.com/attributions">CARTO</a>',
            subdomains: "abcd",
            maxZoom: 20,
          },
        ).addTo(mapInstanceRef.current);
      } catch (e) {
        console.error("Map initialization error:", e);
      }
    }
  }, [isLeafletLoaded]);

  useEffect(() => {
    if (mapInstanceRef.current && pathCoordinates.length > 0 && window.L) {
      const L = window.L;
      if (!playbackMarkerRef.current) {
        playbackMarkerRef.current = L.marker(pathCoordinates[0]).addTo(
          mapInstanceRef.current,
        );
      } else {
        playbackMarkerRef.current.setLatLng(pathCoordinates[0]);
      }
    }
  }, [pathCoordinates]);

  useEffect(() => {
    if (!isPlaying || pathCoordinates.length === 0) return;

    const baseInterval = 50;

    const interval = setInterval(() => {
      setPlayIndex((prev) => {
        if (prev >= pathCoordinates.length - 1) {
          clearInterval(interval);
          setIsPlaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, baseInterval / speed);

    return () => clearInterval(interval);
  }, [isPlaying, pathCoordinates, speed]);

  useEffect(() => {
    if (
      !playbackMarkerRef.current ||
      pathCoordinates.length === 0 ||
      playIndex === 0
    )
      return;

    const start = pathCoordinates[playIndex - 1];
    const end = pathCoordinates[playIndex];

    const duration = 500;

    animateMarker(start, end, duration);
  }, [playIndex, pathCoordinates]);
  useEffect(() => {
    setPlayIndex(0);
    setIsPlaying(false);
  }, [pathCoordinates]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !window.L) return;
    const L = window.L;
    if (polylineRef.current) {
      map.removeLayer(polylineRef.current);
      polylineRef.current = null;
    }
    markersRef.current.forEach((m) => map.removeLayer(m));
    markersRef.current = [];

    if (pathCoordinates && pathCoordinates.length > 0) {
      polylineRef.current = L.polyline(pathCoordinates, {
        color: "#6366f1",
        weight: 5,
        opacity: 0.8,
        lineJoin: "round",
      }).addTo(map);

      const createMarkerIcon = (color) =>
        L.divIcon({
          className: "bg-transparent",
          html: `<div style="background-color: ${color}; width: 14px; height: 14px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
          iconSize: [14, 14],
          iconAnchor: [7, 7],
        });
      const startCoord = pathCoordinates[0];
      const endCoord = pathCoordinates[pathCoordinates.length - 1];
      const startMarker = L.marker(startCoord, {
        icon: createMarkerIcon("#10b981"),
      }).addTo(map);
      startMarker.bindPopup(`
        <div style="font-family: sans-serif; font-size: 12px;">
          <strong style="color: #059669;">Start Point</strong><br/>
          ${selectedTrip?.startTime || ""}<br/>
          ${selectedTrip?.beginLocation || ""}
        </div>
      `);
      markersRef.current.push(startMarker);
      const endMarker = L.marker(endCoord, {
        icon: createMarkerIcon("#ef4444"),
      }).addTo(map);
      endMarker.bindPopup(`
        <div style="font-family: sans-serif; font-size: 12px;">
          <strong style="color: #dc2626;">End Point</strong><br/>
          ${selectedTrip?.endTime || ""}<br/>
          ${selectedTrip?.endLocation || ""}
        </div>
      `);
      markersRef.current.push(endMarker);
      const bounds = L.latLngBounds(pathCoordinates);
      map.fitBounds(bounds, { padding: [50, 50] });
    }

    setTimeout(() => map.invalidateSize(), 200);
  }, [pathCoordinates, selectedTrip]);

  return (
    <div className="map-wrapper-trip">
      <div className="playback-menu">
        <button className="playback-btn" onClick={() => setIsPlaying(true)}>
          ▶
        </button>

        <button className="playback-btn" onClick={() => setIsPlaying(false)}>
          ⏸
        </button>

        <button
          className="playback-btn"
          onClick={() => {
            setPlayIndex(0);
            setIsPlaying(false);

            if (playbackMarkerRef.current && pathCoordinates.length > 0) {
              const firstPoint = pathCoordinates[0];
              playbackMarkerRef.current.setLatLng(firstPoint);

              const map = mapInstanceRef.current;
              if (map) {
                const bounds = window.L.latLngBounds(pathCoordinates);
                map.fitBounds(bounds, { padding: [50, 50] });
              }
            }
          }}
        >
          🔄
        </button>

        <div className="speed-control">
          <select
            value={speed}
            onChange={(e) => setSpeed(Number(e.target.value))}
            className="speed-dropdown"
          >
            <option value={0.5}>0.5x</option>
            <option value={1}>1x</option>
            <option value={2}>2x</option>
            <option value={5}>5x</option>
          </select>
        </div>
      </div>

      <div ref={mapContainerRef} className="map-container-trip" />

      {!isLeafletLoaded && (
        <div
          className="state-msg-trip"
          style={{ position: "absolute", inset: 0, zIndex: 20 }}
        >
          <span>Loading Map Engine...</span>
        </div>
      )}
    </div>
  );
};

const DetailItem = ({ Icon, label, value }) => (
  <div className="detail-item-trip">
    <div className="detail-icon-trip">
      <Icon size={18} />
    </div>
    <div style={{ overflow: "hidden" }}>
      <span
        style={{
          display: "block",
          fontSize: "10px",
          fontWeight: "bold",
          color: "#94a3b8",
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      <div
        style={{
          fontWeight: "600",
          color: "#334155",
          fontSize: "14px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={String(value)}
      >
        {value}
      </div>
    </div>
  </div>
);

const TripDetailsCard = ({
  trip,
  startAddress,
  endAddress,
  onBack,
  onDelete,
}) => (
  <div className="details-panel-trip">
    <div className="details-header-trip">
      <button onClick={onBack} className="btn-back-trip">
        <ArrowLeft size={16} /> Back
      </button>
      <div style={{ display: "flex", gap: "8px" }}>
        <button
          onClick={() => onDelete(trip.TripID)}
          className="btn-delete-trip"
          title="Delete"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>

    <div className="details-content-trip">
      <h2
        style={{
          fontSize: "1.25rem",
          fontWeight: "bold",
          color: "#1e293b",
          marginBottom: "4px",
        }}
      >
        Trip Details
      </h2>
      <p
        style={{
          fontSize: "0.75rem",
          color: "#94a3b8",
          fontFamily: "monospace",
          marginBottom: "1.5rem",
        }}
      >
        {trip.TripID}
      </p>

      <div className="detail-grid-trip">
        <DetailItem Icon={Car} label="Vehicle" value={trip.vehicle} />
        <DetailItem
          Icon={Route}
          label="Distance"
          value={`${trip.distance?.toFixed(2) || 0} km`}
        />
        <DetailItem Icon={Timer} label="Duration" value={trip.duration} />
        <DetailItem
          Icon={Gauge}
          label="Avg Speed"
          value={`${trip.avgSpeed?.toFixed(1) || 0} km/h`}
        />
        <DetailItem
          Icon={Maximize}
          label="Max Speed"
          value={`${trip.maxSpeed?.toFixed(1) || 0} km/h`}
        />
        <DetailItem
          Icon={StopCircle}
          label="Idle Duration"
          value={`${trip.idleTime?.toFixed(1) || 0} Min`}
        />
      </div>

      <div className="timeline-card-trip">
        <div className="timeline-line-trip"></div>

        <div className="timeline-point-trip" style={{ "--color": "#10b981" }}>
          <div
            className="dot-trip"
            style={{ backgroundColor: "#10b981" }}
          ></div>
          <div>
            <div
              style={{
                fontWeight: "bold",
                color: "#334155",
                fontSize: "0.875rem",
              }}
            >
              {trip.startTime}
            </div>
            <p
              style={{
                color: "#64748b",
                fontSize: "0.75rem",
                marginTop: "4px",
              }}
            >
              {startAddress || "Location N/A"}
            </p>
          </div>
        </div>

        <div className="timeline-point-trip" style={{ "--color": "#ef4444" }}>
          <div
            className="dot-trip"
            style={{ backgroundColor: "#ef4444" }}
          ></div>
          <div>
            <div
              style={{
                fontWeight: "bold",
                color: "#334155",
                fontSize: "0.875rem",
              }}
            >
              {trip.endTime}
            </div>
            <p
              style={{
                color: "#64748b",
                fontSize: "0.75rem",
                marginTop: "4px",
              }}
            >
              {endAddress || "Location N/A"}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default function TripsDashboard() {
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [showTripDetails, setShowTripDetails] = useState(false);
  const [activeFilter, setActiveFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedVehicle, setSelectedVehicle] = useState("All");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [tripsPerPage] = useState(10);
  const [selectedVehicleType, setSelectedVehicleType] = useState(
    "Select Vehicle Group",
  );
  const queryClient = useQueryClient();
  const token = localStorage.getItem("token");
  const tripStatuses = [
    "All",
    "Completed",
    "In progress",
    "Expired",
    "Deleted",
  ];
  const formatISTDateTime = (dateString) => {
    if (!dateString) return "N/A";

    return new Date(dateString).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };
  const [startAddress, setStartAddress] = useState("");
  const [endAddress, setEndAddress] = useState("");
  const { data: vehicleList = [] } = useQuery({
    queryKey: ["vehicles", token],
    queryFn: async () => {
      if (!token) return [];
      const response = await axios.get(
        "https://api.yellowmatrix.co.in/vehicles",
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data.map((vehicle) => ({
        VehicleID: vehicle.VehicleID,
        VehicleType: vehicle.VehicleType,
        PowerTrain: vehicle.Powertrain,
      }));
    },
    enabled: !!token,
    staleTime: 10 * 60 * 1000,
  });
  const {
    data: tripData,
    isLoading: isLoadingTrips,
    isError,
  } = useQuery({
    queryKey: ["trips", token, currentPage, tripsPerPage, selectedVehicle],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      if (!token) throw new Error("Authentication missing");

      const params = { page: currentPage, per_page: tripsPerPage };
      if (selectedVehicle !== "All") params.vehicleid = selectedVehicle;
      const response = await axios.get("https://api.yellowmatrix.co.in/trips", {
        headers: { Authorization: `Bearer ${token}` },
        params: params,
      });
      const rawTrips = response.data.data || [];
      const mappedTrips = rawTrips.map((trip) => ({
        TripID: trip.TripID,
        name: trip.TripID.replace("_", ": "),
        status: "Completed",
        vehicle: trip.VehicleID,

        vehicleType: vehicleTypeMap[trip.VehicleID]?.vehicleType || "Other",

        powerTrain: vehicleTypeMap[trip.VehicleID]?.powerTrain || "Unknown",

        startTime: formatISTDateTime(trip.Beginning.BeginTime),
        endTime: formatISTDateTime(trip.Ending.EndTime),
        rawStartTime: new Date(trip.Beginning.BeginTime),
        duration:
          trip.TripTime !== undefined
            ? formatDuration(trip.TripTime)
            : calculateDuration(trip.Beginning.BeginTime, trip.Ending.EndTime),
        beginLocation: trip.Beginning.BeginLocation,
        endLocation: trip.Ending.EndLocation,
        avgSpeed: trip.Speed?.AvgSpeed,
        maxSpeed: trip.Speed?.MaxSpeed,
        distance: trip.TripDistance,
        idleTime: trip.Idletime,
      }));

      return {
        trips: mappedTrips,
        pagination: response.data.pagination || {},
      };
    },
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
  });

  const trips = useMemo(() => tripData?.trips || [], [tripData]);
  const paginationInfo = tripData?.pagination || {};

  const vehicleTypeMap = useMemo(() => {
    const map = {};

    vehicleList.forEach((vehicle) => {
      map[vehicle.VehicleID] = {
        vehicleType: vehicle.VehicleType,
        powerTrain: vehicle.Powertrain,
      };
    });

    return map;
  }, [vehicleList]);
  const { data: tripPath = [] } = useQuery({
    queryKey: ["tripPath", selectedTrip?.TripID],
    queryFn: async () => {
      if (!token || !selectedTrip?.TripID) return [];
      const response = await axios.get(
        `https://api.yellowmatrix.co.in/trips/${selectedTrip.TripID}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.data?.path) return [];
      return response.data.path
        .filter((p) => {
          const lat = parseFloat(p.Latitude);
          const lng = parseFloat(p.Longitude);
          return lat !== 0 && lng !== 0 && !isNaN(lat) && !isNaN(lng);
        })
        .map((p) => [parseFloat(p.Latitude), parseFloat(p.Longitude)]);
    },
    enabled: !!selectedTrip?.TripID && !!token,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!tripPath || tripPath.length === 0) return;

    let cancelled = false;

    const loadAddresses = async () => {
      const [startLat, startLng] = tripPath[0];
      const [endLat, endLng] = tripPath[tripPath.length - 1];

      setStartAddress("Fetching address...");
      setEndAddress("Fetching address...");

      const startAddr = await reverseGeocode(startLat, startLng);
      const endAddr = await reverseGeocode(endLat, endLng);

      if (!cancelled) {
        setStartAddress(startAddr);
        setEndAddress(endAddr);
      }
    };

    loadAddresses();

    return () => {
      cancelled = true;
    };
  }, [tripPath]);
  const deleteTripMutation = useMutation({
    mutationFn: async (tripId) => {
      await axios.delete(`https://api.yellowmatrix.co.in/trips/${tripId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["trips"]);
      setShowTripDetails(false);
      setSelectedTrip(null);
    },
    onError: (err) => alert("Failed to delete trip: " + err.message),
  });

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this trip?")) {
      deleteTripMutation.mutate(id);
    }
  };
  const filteredTrips = useMemo(() => {
    return trips.filter((trip) => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        trip.TripID?.toLowerCase().includes(searchLower) ||
        trip.vehicle?.toLowerCase().includes(searchLower);

      const matchesStatus =
        activeFilter === "All" || trip.status === activeFilter;

      let matchesDate = true;
      if (startDate) {
        matchesDate = matchesDate && trip.rawStartTime >= new Date(startDate);
      }
      if (endDate) {
        const endDay = new Date(endDate);
        endDay.setHours(23, 59, 59);
        matchesDate = matchesDate && trip.rawStartTime <= endDay;
      }

      const matchesVehicleType =
        selectedVehicleType === "Select Vehicle Group" ||
        trip.vehicleType === selectedVehicleType ||
        (selectedVehicleType === "Electric" &&
          trip.powerTrain === "Electric") ||
        (selectedVehicleType === "Other" &&
          !["2W", "3W", "4W"].includes(trip.vehicleType) &&
          trip.powerTrain !== "Electric");

      return (
        matchesSearch && matchesStatus && matchesDate && matchesVehicleType
      );
    });
  }, [
    trips,
    searchTerm,
    activeFilter,
    startDate,
    endDate,
    selectedVehicleType,
  ]);

  const handleTripClick = (trip) => {
    setSelectedTrip(trip);
    setShowTripDetails(true);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= (paginationInfo.total_pages || 1)) {
      setCurrentPage(newPage);
    }
  };

  return (
    <div className="app-container-trip">
      <header className="app-header-trip">
        <div className="brand-trip">
          <div className="logo-box-trip">
            <MapIcon size={20} />
          </div>
          <h1 className="brand-title-trip hidden-mobile-trip">
            Trip Analytics
          </h1>
        </div>
        <div className="vehicle-select-wrapper-trip">
          <Truck className="icon-select-left-trip" size={16} />
          <select
            className="vehicle-select-trip"
            value={selectedVehicle}
            onChange={(e) => {
              setSelectedVehicle(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="All">All Vehicles</option>
            {vehicleList.map((v) => (
              <option key={v.VehicleID} value={v.VehicleID}>
                {v.VehicleID}
              </option>
            ))}
          </select>
          <ChevronRight className="icon-select-right-trip" size={12} />
        </div>
      </header>

      <div className="filter-bar-trip">
        <div className="status-filters-trip">
          {tripStatuses.map((status) => {
            const StatusIcon = getStatusIcon(status);
            const isActive = activeFilter === status;
            return (
              <button
                key={status}
                onClick={() => setActiveFilter(status)}
                className={`filter-btn-trip ${
                  isActive ? "active-trip" : "inactive-trip"
                }`}
              >
                <StatusIcon size={14} />
                {status}
              </button>
            );
          })}
        </div>

        <div className="search-date-group-trip">
          <div className="vehicle-type-filter-trip">
            <select
              value={selectedVehicleType}
              onChange={(e) => setSelectedVehicleType(e.target.value)}
              className="vehicle-type-select-trip"
            >
              <option value="Select Vehicle Group">Select Vehicle Group</option>

              <option value="2W">2W</option>
              <option value="3W">3W</option>
              <option value="4W">4W</option>
              <option value="Electric">Electric</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div className="search-box-trip">
            <Search
              className="text-slate-400"
              size={16}
              style={{ marginRight: "8px", color: "#94a3b8" }}
            />
            <input
              type="text"
              placeholder="Search ID or Vehicle..."
              className="search-input-trip"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="date-box-trip">
            <Calendar
              size={14}
              style={{ marginLeft: "8px", color: "#94a3b8" }}
              className="hidden-mobile-trip"
            />
            <input
              type="date"
              className="date-input-trip"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <span style={{ color: "#cbd5e1" }}>-</span>
            <input
              type="date"
              className="date-input-trip"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
      </div>
      <main className="main-grid-trip">
        <aside className="sidebar-trip">
          {isLoadingTrips ? (
            <div className="state-msg-trip">
              <div className="spinner-trip"></div>
              <span>Loading Trips...</span>
            </div>
          ) : isError ? (
            <div className="state-msg-trip" style={{ color: "#ef4444" }}>
              <AlertCircle size={24} style={{ marginBottom: "8px" }} />
              <p>Error loading data. Check your connection or login.</p>
            </div>
          ) : showTripDetails && selectedTrip ? (
            <TripDetailsCard
              trip={selectedTrip}
              startAddress={startAddress}
              endAddress={endAddress}
              onBack={() => setShowTripDetails(false)}
              onDelete={handleDelete}
            />
          ) : (
            <>
              <div className="sidebar-header-trip">
                <span>Trips ({filteredTrips.length})</span>
                <span>Stats</span>
              </div>

              <div className="trip-list-trip">
                {filteredTrips.length === 0 ? (
                  <div className="state-msg-trip">
                    <Search
                      size={24}
                      style={{ marginBottom: "8px", opacity: 0.5 }}
                    />
                    No trips found.
                  </div>
                ) : (
                  filteredTrips.map((trip) => {
                    const isSelected = selectedTrip?.TripID === trip.TripID;
                    return (
                      <div
                        key={trip.TripID}
                        onClick={() => handleTripClick(trip)}
                        className={`trip-card-trip ${
                          isSelected ? "selected-trip" : ""
                        }`}
                      >
                        <div className="trip-card-top-trip">
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <div className="trip-icon-box-trip">
                              <Route size={18} />
                            </div>
                            <div>
                              <h3
                                style={{
                                  margin: 0,
                                  fontWeight: 600,
                                  fontSize: "0.875rem",
                                  color: "#334155",
                                }}
                              >
                                {trip.vehicle}
                              </h3>
                              <div
                                style={{
                                  fontSize: "0.75rem",
                                  color: "#94a3b8",
                                  marginTop: "2px",
                                }}
                              >
                                {trip.startTime}
                              </div>
                            </div>
                          </div>
                          <ChevronRight
                            size={14}
                            style={{
                              color: isSelected ? "#818cf8" : "#cbd5e1",
                            }}
                          />
                        </div>

                        <div className="trip-stats-trip">
                          <span className="stat-item-trip">
                            <Timer size={12} /> {trip.duration}
                          </span>
                          <span className="stat-item-trip">
                            <Gauge size={12} />{" "}
                            {trip.distance?.toFixed(1) || "0"} km
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <div className="pagination-trip">
                <button
                  disabled={currentPage <= 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                  className="page-btn-trip"
                >
                  <ChevronLeft size={16} color="#64748b" />
                </button>
                <span
                  style={{
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: "#64748b",
                  }}
                >
                  Page {currentPage} of {paginationInfo.total_pages || 1}
                </span>
                <button
                  disabled={currentPage >= (paginationInfo.total_pages || 1)}
                  onClick={() => handlePageChange(currentPage + 1)}
                  className="page-btn-trip"
                >
                  <ChevronRight size={16} color="#64748b" />
                </button>
              </div>
            </>
          )}
        </aside>
        <section
          className="map-section-trip"
          style={{
            display:
              showTripDetails && window.innerWidth < 1024
                ? "flex"
                : window.innerWidth < 1024
                  ? "none"
                  : "block",
          }}
        >
          <TripMap pathCoordinates={tripPath} selectedTrip={selectedTrip} />

          {!selectedTrip && (
            <div className="map-placeholder-trip">
              <div
                style={{
                  width: 64,
                  height: 64,
                  background: "white",
                  borderRadius: 16,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                  marginBottom: 16,
                }}
              >
                <MapIcon size={32} color="#cbd5e1" />
              </div>
              <p>Select a trip to view the route</p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
